import { hashHistory } from 'react-router';
import jack from 'jack-logs';
import { sendSignup } from '../../lib/Intercom';
import airbrake from '../../lib/Airbrake';
import { doRequest } from '../requests/actions';
import RequestError from '../../lib/errors/RequestError';
import CustomError from '../../lib/errors/CustomError';
import * as lib from './lib';
import { getSettings } from '../settings/organisation/actions';
import { get as getEmployees }
  from '../employees/actions';
import { get as getShiftDetails } from '../shiftDetails/actions';
import { add as addNotification } from '../messages/notifications/actions';
import { register as registerDevice } from '../device/actions';
import { setMagicLink } from '../config/actions';
import { signUpClearState } from '../signUp/actions';

export const actions = {
  GET_ACCESS_TOKEN: 'ACCOUNT_GET_ACCESS_TOKEN',
  SET_ACCESS_TOKEN: 'ACCOUNT_SET_ACCESS_TOKEN',
  SET_AUTH_USER: 'ACCOUNT_SET_AUTH_USER',
  CLEAR_AUTH_USER: 'ACCOUNT_CLEAR_AUTH_USER',
  SET_AUTH_USER_ORGS: 'ACCOUNT_SET_AUTH_USER_ORG',
  CLEAR: 'ACCOUNT_CLEAR',
  SET_LOCATIONS: 'ACCOUNT_SET_LOCATIONS',
  ADD_LOCATION: 'ACCOUNT_ADD_LOCATION',
};

export function setAccessToken(token) {
  return {
    type: actions.SET_ACCESS_TOKEN,
    value: token,
  };
}

export function getAccessToken(username, password) {
  return async (dispatch) => {
    if (window.NATIVE_INTERCOM) {
      window.intercom.reset();
    }
    const headers = new Headers();
    headers.append('Cache-Control', 'no-cache');
    let setupCode = false;

    // this pattern sucks, but then this app is deprecated
    // https://trello.com/c/YglfNcRh/6416-desktop-timeclock-always-ask-for-re-authenticate-to-sync-the-pending-clockins-outs
    if (username && username.length >= 8 && !Number.isNaN(parseInt(username, 10))) {
      // this is a tanda access code
      headers.append('X-Tanda-Access-Code', true);
      setupCode = true;
    }
    // this needs to be a form
    const form = new FormData();
    form.append('username', username);
    form.append('password', password);
    form.append(
      'scope',
      'me roster timesheet department user cost leave unavailability datastream device settings sms',
    );
    form.append('grant_type', 'password');

    const res = await fetch(`${__BASE_URL__}/api/oauth/token`, {
      headers,
      method: 'POST',
      body: form,
    });
    if (!res.ok) {
      jack.error('Auth Request Failed', await res.json(), username);
      if (res.status === 401) {
        // username/password error
        let message = 'Your username and password are incorrect. Please check them and try again';
        if (setupCode) {
          message = 'Incorrect setup code, please try again.';
        }
        throw new CustomError('AuthError', message);
      }
      throw new RequestError(res.status);
    }
    const data = await res.json();
    jack.dev.log(data);
    if (!data.access_token) {
      throw new CustomError('AccessTokenError', 'Failed to generate an access token.  Please' +
        ' contact support.');
    }
    dispatch(setAccessToken(data.access_token));
  };
}

export function registerOnIntercom() {
  return (dispatch, getState) => {
    if (window.NATIVE_INTERCOM) {
      const user = getState().getIn(['account', 'authUser']).toJS();
      if (!user) { return; } // get out early if we don't have this
      window.intercom.registerIdentifiedUser({ userId: user.id });
      window.intercom.updateUser({ name: user.name, companies: user.organisations });
    }
  };
}

export function signUpRecordConversion() {
  return (dispatch, getState) => {
    const form = getState().get('signUp').toJS();

    if (form) {
      if (window.facebookConnectPlugin) {
        window.facebookConnectPlugin.logEvent('TimeClockSignup', {
          name: form.name.value,
          email: form.email.value,
          phone: form.phone.value,
          company: form.businessName.value,
          staffCount: form.staffCount.value,
        });
      }
    }
  };
}

export function setAuthUser(userInfo) {
  return {
    type: actions.SET_AUTH_USER,
    value: userInfo,
  };
}

export function setAuthUserOrgs(organisation) {
  return {
    type: actions.SET_AUTH_USER_ORGS,
    value: organisation,
  };
}

export function getAuthUser() {
  return async (dispatch) => {
    const me = await dispatch(doRequest({ endpoint: '/users/me' }));
    if (!me) {
      throw new CustomError(
        'AuthError',
        'There was a problem signing in.  Please contact support.',
      );
    }
    const userProfile = await dispatch(doRequest({ endpoint: `/users/${me.id}` }));
    dispatch(setAuthUser(userProfile));
    dispatch(setAuthUserOrgs(me.organisations));
    return me;
  };
}

export function setLocations(locations) {
  return {
    type: actions.SET_LOCATIONS,
    value: locations,
  };
}

export function getLocations() {
  return async (dispatch) => {
    const locations = await dispatch(doRequest({ endpoint: '/locations' }));
    return dispatch(setLocations(locations || []));
  };
}

export function authAccount(username, password) {
  return async (dispatch) => {
    try {
      await dispatch(getAccessToken(username, password));
      await dispatch(getAuthUser());
      await dispatch(getLocations());
      dispatch(getSettings());
    } catch (e) {
      if (e.name === 'RequestError' || e.name === 'AuthError' || e.name === 'AccessTokenError') {
        dispatch(addNotification({ type: 'error', body: e.message }));
      } else {
        airbrake.notify(e);
        dispatch(addNotification({
          type: 'error',
          body: 'An unexpected error has occurred.' +
        '  Please try again later.',
        }));
      }
      return false;
    }
    return true;
  };
}

export function clear() {
  return {
    type: actions.CLEAR,
  };
}

export function clearSelf() {
  return (dispatch) => {
    dispatch(clear());
    dispatch(signUpClearState());
    hashHistory.push('/setup');
  };
}

export function addLocation(location) {
  return {
    type: actions.ADD_LOCATION,
    value: location,
  };
}

export function createLocation(location) {
  return async (dispatch) => {
    const {
      name, address, latitude, longitude,
    } = location;

    if (!name || name === '' || !(typeof (name) === 'string')) {
      throw new Error('location name is invalid');
    }

    if (address) {
      if (!address || address === '' || typeof address !== 'string') {
        throw new Error('address is invalid');
      }
      if (!latitude || latitude === '' || typeof latitude !== 'number') {
        throw new Error('latitude is invalid');
      }
      if (!longitude || longitude === '' || typeof longitude !== 'number') {
        throw new Error('longitude is invalid');
      }
    }

    const newLocation = await dispatch(doRequest({
      endpoint: '/locations',
      method: 'POST',
      data: {
        name,
        address,
        latitude,
        longitude,
      },
    }));

    if (newLocation === null) {
      throw new Error('Failed to create location.');
    }

    dispatch(addLocation(newLocation));
    return newLocation;
  };
}

/**
 * create a new tanda account
 * @param {Object} account containing the fields for a new sign up.
 * @param {string} account.name the name of the new user.
 * @param {string} account.email the email of the new user.
 * @param {string} account.phone the ph # of the new user.
 * @param {string} account.password the pass of the new user.
 * @param {string} account.businessName the business_name of the new user.
 * @returns {Promise} Resolves after access token is set.
 */
export function create(account) {
  return async (dispatch) => {
    lib.validateAccount(account); // will throw the error if they occur
    const form = lib.buildAccountData(account);

    const newAccount = await dispatch(doRequest({
      endpoint: '/sign_up',
      method: 'POST',
      data: form,
    }));

    dispatch(setAccessToken(newAccount.access_token));
    dispatch(setMagicLink(newAccount.magic_link));
    return newAccount.access_token;
  };
}

export function sendIntercomRequest(form) {
  return (dispatch, getState) => {
    const request = lib.buildIntercom(form, getState());
    sendSignup(request);
  };
}

export function signUp(form) {
  return async (dispatch) => {
    await dispatch(create(form)); // create a new account - these helpers can deal with `form`

    const [authUser, location] = await Promise.all([
      dispatch(getAuthUser()),
      dispatch(createLocation(lib.buildLocationRequest(form))),
    ]);

    await dispatch(registerDevice(...lib.buildDeviceRequest(location.id, form)));

    // Identify newly created user
    if (window.analytics && window.analytics.identify && authUser.user_ids[0] != null) {
      window.analytics.identify(authUser.user_ids[0].toString(), {
        name: authUser.name,
        email: authUser.email,
        company: authUser.organisation,
        organisation_id: authUser.organisation_id,
        signup_size: form.staffCount.value,
      });
    }

    // once staff are created both of these can run concurrently
    await Promise.all([
      dispatch(getEmployees()), // not the most ideal, but this will put the auth user in too
      dispatch(getSettings()), // get some more user information
    ]);

    // we don't need to wait for this result as it's not needed immediately
    dispatch(getShiftDetails());

    // dispatch intercom boot message
    dispatch(sendIntercomRequest(form));
  };
}

export function refresh() {
  return (dispatch) => {
    dispatch(getAuthUser()).catch(() => {}); // do nothing
  };
}



// WEBPACK FOOTER //
// ./src/store/account/actions.js